<template>
  <TransactionListTemplate
    :screenConfig="setScreenConfig"
    :rowClick="rowClickCallBack"
    :setLoading="setLoading"
    :ref="'complaint-list'"
    :class="'complaint-list'"
    :persistenceKey="getPersistenceKey"
  />

  <!-- :persistenceKey="'getPersistenceKey'" -->
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";

import TransactionListTemplate from "../../templates/transaction-list";
import { FeatureStorage } from "../../_services/storage";
import { CustomerComplaintService } from "../../_services/api/quality-control/customer-complaint.service";
import {
  ComplaintListTabs,
  GetIndexByTabName,
  ComplaintTabFilters,
} from "../../_models/tabdata/quality-control/complaint-tabs";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";

export default {
  props: {},
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    TransactionListTemplate,
  },
  data() {
    return {
      selectedTabId: "all",
      setLoading: false,
    };
  },
  computed: {
    setScreenConfig() {
      const selectedFilter = ComplaintTabFilters[this.selectedTabId] || "";

      return {
        title: "Customer Complaints",
        exportFileName: "Customer Complaints List",
        selectedIndex: GetIndexByTabName(this.selectedTabId),
        tabs: ComplaintListTabs(),
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "CustomerComplaintList",
              // items: [],
              filter: selectedFilter,
              serverPaging: true,
              allowGrouping: false,
            },
            noDataMessage: {
              addNew: {
                class: "add-new",
                title: "No Complaints",
                message: "Get Started, Create a Complaint",
              },
            },
          },
          toolbar: {
            search: false,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/customer-complaints/create",
                clickAction: {},
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "CustomerComplaints",
                  "create",
                ),
                // disabled: true, //  to be enabled when create complaint screen is ready
              },
            ],
          },
          columns: [
            {
              screenField: "Code",
              label: "Complaint #",
              gridDataField: "ComplaintCode",
              sortOrder: "desc",
              sortIndex: 0,
              minWidth: 120,
              width: 120,
            },
            {
              screenField: "Customer",
              label: "Customer",
              gridDataField: "CustomerName",
              filterLabel: "Customer",
              filterIndex: 2,
              allowHiding: false,
              width: "auto",
            },
            {
              screenField: "DateCreated",
              label: "Date Raised",
              gridDataField: "DateRaised",
              format: "date",
              filterLabel: "Date Raised",
              filterIndex: 6,
              filterDataField: "DateRaised",
              minWidth: ColumnWidth.Date.minWidth,
              width: ColumnWidth.Date.width,
            },
            {
              label: "# Products/ Batches",
              dataField: "ProductCount",
              allowSearch: false,
              allowSorting: false,
              cellTemplate: (data, info) => {
                return (data.innerHTML = `<div class="ProductCount">
                        <span class="count-chip">
                          ${info.data.ProductCount} ${
                  info.data.ProductCount > 1 ? "Products" : "Product"
                }
                        </span> </br>
                      <span class="count-chip">
                        ${info.data.BatchCount} ${
                  info.data.BatchCount > 1 ? "Batches" : "Batch"
                }
                      </span>
                    </div>`);
              },
              exportFields: [
                {
                  dataField: "ProductCount",
                  label: "# Products",
                  showInColumnChooser: false,
                },
                {
                  dataField: "BatchCount",
                  label: "# Batch",
                  showInColumnChooser: false,
                },
              ],
              width: 130,
            },
            {
              label: "# Actions",
              dataField: "ActionCount",
              allowSearch: false,
              allowSorting: false,
              width: 100,
            },
            // Asked to remove Work Order column from the grid, but keeping the code here in case we need to add it back in future
            // {
            //   label: "Work Order",
            //   dataField: "MaxWorkOrder",
            //   allowSearch: false,
            //   allowSorting: false,
            //   width: 100,
            // },
            {
              screenField: "Status",
              label: "Status",
              gridDataField: "Status",
              filterLabel: "Status",
              filterDataField: "Status",
              filterIndex: this.selectedTabId === "all" ? 1 : undefined, // show status filter as default only for 'All' tab
              cellTemplate: "Status",
              dataSourceConfig: {
                data: [
                  { key: "Open", value: "Open" },
                  { key: "Investigating", value: "Investigating" },
                  { key: "Resolved", value: "Resolved" },
                  { key: "Cancelled", value: "Cancelled" },
                ],
              },
              valueExpr: "key",
              displayExpr: "value",
            },
            {
              screenField: "Batch",
              type: "Text",
              filterLabel: "Batch",
              filterDataField: "BatchList",
              filterIndex: 3,
              visible: false,
              showInColumnChooser: false,
              allowExport: false,
            },
            {
              screenField: "WorkOrder",
              type: "Text",
              filterLabel: "Work Order",
              filterDataField: "WorkOrderList",
              filterIndex: 4,
              visible: false,
              showInColumnChooser: false,
              allowExport: false,
            },
            {
              screenField: "Product",
              filterLabel: "Product",
              filterDataField: "ProductList",
              filterIndex: 5,
              visible: false,
              showInColumnChooser: false,
              filterDataRelationship: "Comma",
              filterOperator: "contains",
              allowExport: false,
            },
            {
              screenField: "ComplaintType",
              label: "Complaint Type",
              filterLabel: "Complaint Type",
              filterIndex: 6,
              filterDataField: "ComplaintTypeCode",
              visible: true,
              showInColumnChooser: true,
              // allowExport: false,
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "ComplaintTypeDescription",
                  },
                ],
                secondary: [
                  {
                    dataField: "ComplaintTypeCode",
                  },
                ],
              },
              exportFields: [
                {
                  dataField: "ComplaintTypeCode",
                  label: "Complaint Type Code",
                },
                {
                  dataField: "ComplaintTypeDescription",
                  label: "Complaint Type Name",
                },
              ],
              valueExpr: "Code",
              displayExpr: "Description",
            },
            {
              screenField: "GridRecordEllipsisMenu",
              cssClass: "padding-7",
              width: 50,
              allowSearch: false,
              ellipsisMenu: {
                itemAction: (data) => {
                  const status = data.data.Status;
                  return [
                    {
                      menuText: "Reopen",
                      data: data,
                      clickAction: this.reopenComplaint,
                      disabled: status !== "Resolved" && status !== "Cancelled",
                    },
                    {
                      menuText: "Cancel Complaint",
                      data: data,
                      clickAction: this.cancelComplaint,
                      disabled: status === "Cancelled" || status === "Resolved",
                    },
                  ];
                },
                keyExpr: "menuText",
                displayExpr: "menuText",
              },
              makeDisabled: () => {
                return !FeatureStorage.checkPermission("CustomerComplaints", "create");
              },
            },
          ],
        },
      };
    },

    getPersistenceKey() {
      return `complaint-list-${this.selectedTabId}`;
    },
  },
  methods: {
    async reopenComplaint(e, grid) {
      let confirmed = await this.$confirm(
        "Do you want to reopen this complaint?",
        "Reopen Complaint"
      );

      if (confirmed) {
        this.setLoading = true;
        await CustomerComplaintService.updateStatus({
          ComplaintCode: e.ComplaintCode,
          Action: "Reopen",
        })
          .then((res) => {
            if (res) this.setLoading = false;
            grid.refresh(true);
          })
          .catch((err) => {
            this.setLoading = false;
            this.$store.dispatch("ui/setServerError", {
              error: err,
              title: "Reopen Complaint Failed",
            });
          });
      }
    },

    async cancelComplaint(e, grid) {
      let confirmed = await this.$confirm(
        "Do you want to cancel this complaint?",
        "Cancel Complaint"
      );

      if (confirmed) {
        this.setLoading = true;
        await CustomerComplaintService.updateStatus({
          ComplaintCode: e.ComplaintCode,
          Action: "Cancel",
        })
          .then((res) => {
            if (res) this.setLoading = false;
            grid.refresh(true);
          })
          .catch((err) => {
            this.setLoading = false;
            this.$store.dispatch("ui/setServerError", {
              error: err,
              title: "Cancel Complaint Failed",
            });
          });
      }
    },

    rowClickCallBack(e) {
      if (
        e.rowType == "data" &&
        e.column.screenField != "Status" &&
        e.column.screenField != "GridRecordEllipsisMenu"
      ) {
        this.onRowClicked(e, {
          view: this,
          page: "customer-complaints",
          mode: "review",
          idProperty: "ComplaintCode",
        });
      }
    },
  },

  created() {
    const tabParam = this.$route.query.tab;
    if (tabParam && ComplaintListTabs().find((t) => t.id === tabParam)) {
      this.selectedTabId = tabParam;
    }
  },
  watch: {
    "$route.query.tab": function (newTab) {
      if (newTab && ComplaintListTabs().find((t) => t.id === newTab)) {
        this.selectedTabId = newTab;
      } else {
        this.selectedTabId = "all";
      }

      this.$nextTick(() => {
        this.$refs["complaint-list"].updateGridViewWithFilters();
      });
    },
  },
};
</script>

<style lang="scss">
.complaint-list {
  .count-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    color: #667085;
    background: #f9fafb;
    border: 1px solid #eaecf0;
    border-radius: 9999px;
    padding: 2px 8px;
    margin-right: 3px;
  }
}
</style>
